import React, { useState, useEffect } from 'react';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { BrokenImage as BrokenImageIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { getFallbackUrls, findWorkingImageUrl } from '../utils/imageUtils';

interface BlogImageProps {
  src: string | null;
  alt: string;
  style?: React.CSSProperties;
  height?: string | number;
  onLoad?: () => void;
  onError?: () => void;
}

const BlogImage: React.FC<BlogImageProps> = ({ 
  src, 
  alt, 
  style, 
  height, // Not used anymore but kept for backward compatibility
  onLoad,
  onError 
}) => {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error' | 'retrying'>('loading');
  const [imageSrc, setImageSrc] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);

  const loadImageWithFallbacks = async (imagePath: string) => {
    if (!imagePath) {
      setImageState('error');
      return;
    }

    setImageState('loading');
    setRetryCount(0);
    
    console.log('🖼️ Loading image with fallbacks:', imagePath);
    
    // Get all possible URLs
    const fallbackUrls = getFallbackUrls(imagePath);
    console.log('🔄 Fallback URLs:', fallbackUrls);
    
    // Find the first working URL
    const workingUrl = await findWorkingImageUrl(fallbackUrls);
    
    if (workingUrl) {
      console.log('✅ Found working image URL:', workingUrl);
      setImageSrc(workingUrl);
      setImageState('loaded');
    } else {
      console.log('❌ No working image URL found');
      setImageState('error');
      onError?.();
    }
  };

  const handleImageLoad = () => {
    console.log('✅ Image loaded successfully:', imageSrc);
    setImageState('loaded');
    onLoad?.();
  };

  const handleImageError = () => {
    console.log('❌ Image failed to load:', imageSrc);
    
    if (retryCount < 3) {
      setRetryCount(prev => prev + 1);
      setImageState('retrying');
      console.log(`🔄 Retrying image load (attempt ${retryCount + 1}/3)`);
      
      setTimeout(() => {
        if (src) {
          loadImageWithFallbacks(src);
        }
      }, 1000 * (retryCount + 1)); // Exponential backoff
    } else {
      console.log('❌ Max retry attempts reached');
      setImageState('error');
      onError?.();
    }
  };

  const handleManualRetry = () => {
    if (src) {
      loadImageWithFallbacks(src);
    }
  };

  // Load image when src changes
  useEffect(() => {
    if (src) {
      loadImageWithFallbacks(src);
    } else {
      setImageState('error');
    }
  }, [src]);

  // Render loading state
  if (imageState === 'loading' || imageState === 'retrying') {
    return (
      <div 
        style={{ 
          width: '100%',
          aspectRatio: '1 / 1',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          ...style 
        }}
      >
        <CircularProgress size={24} />
        {imageState === 'retrying' && (
          <span style={{ marginLeft: 8, fontSize: '0.875rem', color: '#666' }}>
            Retrying... ({retryCount}/3)
          </span>
        )}
      </div>
    );
  }

  // Render error state
  if (imageState === 'error') {
    return (
      <div 
        style={{ 
          width: '100%',
          aspectRatio: '1 / 1',
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '1px dashed #ccc',
          borderRadius: '8px',
          ...style 
        }}
      >
        <BrokenImageIcon style={{ fontSize: 48, color: '#999', marginBottom: 8 }} />
        <span style={{ fontSize: '0.875rem', color: '#666', marginBottom: 8 }}>
          Image not available
        </span>
        <Tooltip title="Retry loading image">
          <IconButton 
            size="small" 
            onClick={handleManualRetry}
            style={{ color: '#666' }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  // Render loaded image
  return (
    <div style={{ 
      width: '100%', 
      aspectRatio: '1 / 1', // Make it square
      overflow: 'hidden',
      borderRadius: '8px',
      position: 'relative',
      ...style 
    }}>
      <img 
        src={imageSrc}
        alt={alt}
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
          transition: 'transform 0.3s ease-in-out'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      />
    </div>
  );
};

export default BlogImage; 